import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { QuestionNumber } from '@doltech/ui/lib/figma/DesignSystemV2/QuestionNumber';
import { Typography } from '@doltech/ui/lib/figma/DesignSystemV2/Typography';
import { TestTypeEnum, zIndex } from '@doltech/utils/lib/constants';
import cl from 'classnames';
import { isNil, noop } from 'lodash';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import styled from 'styled-components';

import { ListeningBottomNavigation } from '../components/BottomNavigation/ListeningBottomNavigation';
import { TestContent } from '../components/TestContent';
import { TestProgress } from '../components/TestProgress';
import { DoTestHeaderDesktop } from '../mobile/DoTest/Reading/Header/DoTestHeaderDesktop';
import { DoTestQuestionType } from '../model/test-question-group.store';
import { TestSectionStoreInstance } from '../model/test-section.store';
import { useTestSubmissionStore } from '../model/test-submissions.hook';
import { ListeningGuidedTour } from './GuidedTour/ListeningGuidedTour';
import { LISTENING_CONTENT_BOX_WIDTH, ListeningTestLayout } from './ListeningTestLayout';
import { TestSubmission, TestSubmissionProps } from './TestSubmission';
import { LayoutPageHamburger } from '@doltech/ui/lib/layouts/pages/LayoutPageHamburger';
import { useGetAuthInfo } from '@doltech/core/lib/auth/withGlobalUserAuthInfo';
import { useUserAuthentication } from '@doltech/ui/lib/figma/Auth/hocs/withUserAuthentication';

const DEFAULT_PADDING = 24;
const REVIEW_CONTAINER_WIDTH = 200;

export interface ListeningTestSubmissionProps extends TestSubmissionProps {
  disableTestInfo?: boolean;
  disableSubmitAction?: boolean;
  isPlayAudio?: boolean;
}

export const ListeningTestSubmission = observer((props: ListeningTestSubmissionProps) => {
  const {
    onSubmit,
    disableTestInfo,
    disableSubmitAction,
    onSubmitWithEvents,
    onQuit = noop,
    onSubmitSuccess,
    isPlayAudio = false,
  } = props;
  const testSubmissionStore = useTestSubmissionStore();
  const currentSection = testSubmissionStore.currentTestSection;
  const { isLoggedIn } = useGetAuthInfo();

  const { forceLoginToProceedAction } = useUserAuthentication();

  React.useEffect(() => {
    if (!isLoggedIn) {
      forceLoginToProceedAction(() => {}, {
        title: '<span>Để <span class="red">làm bài</span> bạn cần phải đăng ký tài khoản!</span>',
        noRedirect: true,
      });
    }
  }, [forceLoginToProceedAction, isLoggedIn]);

  if (isNil(currentSection) || testSubmissionStore.initialed === false) return null;
  const buildHeaderText = () =>
    testSubmissionStore.testType === TestTypeEnum.FULL_TEST
      ? 'IELTS Online Test'
      : 'IELTS Listening Practice Test';

  return (
    <TestSubmission
      onSubmit={onSubmit}
      onSubmitWithEvents={onSubmitWithEvents}
      disableSubmitAction={disableSubmitAction}
      enableEvent
    >
      <LayoutPageHamburger
        fullHeight
        header={
          <DoTestHeaderDesktop
            showQuitButton
            text={buildHeaderText()}
            onQuit={onQuit}
            testName={testSubmissionStore.name}
            skill={testSubmissionStore.skill}
            isReviewing={testSubmissionStore.isReviewing}
          />
        }
        footer={
          <ListeningBottomNavigation
            isPlayAudio={isPlayAudio}
            className={cl('bottom-navigation', 'layout-footer')}
            disableTestInfo={disableTestInfo}
            disableSubmitAction={disableSubmitAction}
            onSubmitWithEvents={onSubmitWithEvents}
            onSubmitSuccess={onSubmitSuccess}
          >
            <TestProgress />
          </ListeningBottomNavigation>
        }
      >
        <ListeningTestLayout expanded={false}>
          <div id="body-container">
            <TestContent id="body-content" />
          </div>
        </ListeningTestLayout>
      </LayoutPageHamburger>
      {/* <DoTestHeaderDesktop
        className="layout-header"
        showQuitButton
        text={buildHeaderText()}
        onQuit={onQuit}
        testName={testSubmissionStore.name}
        skill={testSubmissionStore.skill}
        isReviewing={testSubmissionStore.isReviewing}
      />

      <ListeningTestLayout expanded={false}>
        <div id="body-container">
          <TestContent id="body-content" />
        </div>
      </ListeningTestLayout>
      <ListeningBottomNavigation
        isPlayAudio={isPlayAudio}
        className={cl('bottom-navigation', 'layout-footer')}
        disableTestInfo={disableTestInfo}
        disableSubmitAction={disableSubmitAction}
        onSubmitWithEvents={onSubmitWithEvents}
        onSubmitSuccess={onSubmitSuccess}
      >
        <TestProgress />
      </ListeningBottomNavigation> */}

      <ListeningGuidedTour />
      <ListeningReview />
    </TestSubmission>
  );
});

/**
 * At last step, when you done all questions (or timed out)
 * This component will be showed up
 */
const ListeningReview = observer(() => {
  const [rect, setRect] = React.useState<DOMRect | null>();
  const store = useTestSubmissionStore();

  /**
   * Get first header -> to align papally with
   */
  React.useEffect(() => {
    const veryFirstHeader = document.body.querySelector('.header-container');
    if (veryFirstHeader) {
      setRect(veryFirstHeader.getBoundingClientRect());
    }
  }, []);

  if (!rect) return null;

  return (
    <PositionContainer className={cl('listening-review-time', { hidden: !store.isReviewing })}>
      <ReviewContainer>
        <ScrollContainer>
          <Heading>
            <Typography.Text variant="bold/20">Review time</Typography.Text>
          </Heading>
          <div>
            {store.testSections.map((section: TestSectionStoreInstance, sectionIdx: number) => {
              const { goToQuestion } = section;
              return (
                <Section key={section.id}>
                  <SectionName>section {sectionIdx + 1}</SectionName>
                  <QuestionNumbers>
                    {section.questionGroups.map((group, groupIndex) =>
                      group.questions.map((question: DoTestQuestionType) =>
                        question.bottomBarQuestionNoes.map(({ no, answered }) => (
                          <QuestionNumber
                            size="desktop"
                            key={question.uuid}
                            onClick={() => {
                              store.goToQuestionAtGroup(sectionIdx, groupIndex);
                              goToQuestion({ groupIndex, questionNo: no });
                            }}
                            className="review-question-number"
                            number={no}
                            state="off"
                            type={answered ? 'answered' : 'normal'}
                          />
                        ))
                      )
                    )}
                  </QuestionNumbers>
                </Section>
              );
            })}
          </div>
        </ScrollContainer>
      </ReviewContainer>
    </PositionContainer>
  );
});

const ScrollContainer = styled.div`
  max-height: calc(100vh - 200px);
`;

const QuestionNumbers = styled.div`
  display: grid;
  grid-template-columns: 24px 24px 24px 24px 24px;
  grid-template-rows: 24px 24px;
  gap: 8px 12px;

  .review-question-number {
    cursor: pointer;
  }
`;

const Heading = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Section = styled.div`
  margin-top: 24px;
  &:first-child {
    margin-top: 12px;
  }
`;

const SectionName = styled.div`
  font-size: 10px;
  color: ${colorsV2.black100};
  font-weight: 700;
  line-height: 16px;
  text-transform: uppercase;
  margin-bottom: 8px;
`;

const ReviewContainer = styled.div`
  width: ${REVIEW_CONTAINER_WIDTH}px;
  height: 464px;
  overflow: auto;
  padding: 16px;
  background: ${colorsV2.white100};
  box-shadow: 0 24px 32px ${colorsV2.blackShadow4}, 0 16px 24px ${colorsV2.blackShadow4},
    0 4px 8px ${colorsV2.blackShadow4}, 0 0 1px ${colorsV2.blackShadow4};
  border-radius: 16px;
  z-index: ${zIndex.LISTENING_REVIEW};

  ::-webkit-scrollbar {
    width: 2px;
  }
`;

/**
 * x position
 */

const PositionContainer = styled.div`
  position: absolute;
  top: 80px;
  left: calc((100vw - ${LISTENING_CONTENT_BOX_WIDTH}px) / 2 - 200px - ${DEFAULT_PADDING}px);
  z-index: ${zIndex.LISTENING_REVIEW};
  &.hidden {
    opacity: 0;
  }
`;
